// Custom credentials provider for Identity Pool federation.
// Handles both User Pool logins (ID token) and social logins (provider tokens).
// Returning undefined = unauthenticated (Amplify has no built-in fallback).
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand
} from '@aws-sdk/client-cognito-identity';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

const cfg = window.heku_aws_config;

let _socialLogins = null; // e.g. { 'accounts.google.com': '<token>' }
let _cachedCredentials = null;
let _cachedIdentityId = null;

const cognitoClient = new CognitoIdentityClient({ region: cfg.Region });
const COGNITO_PROVIDER = `cognito-idp.${cfg.Region}.amazonaws.com/${cfg.UserPoolId}`;

// Set social-provider tokens for the next Identity Pool federation
export function federateWithToken(logins) {
  _socialLogins = logins;
  _cachedCredentials = null; // force refresh
}

// Clear federation state on sign-out
export function clearFederation() {
  _socialLogins = null;
  _cachedCredentials = null;
  _cachedIdentityId = null;
}

// Cached federated identity ID, if any
export function getFederatedIdentityId() {
  return _cachedIdentityId;
}

async function _resolveIdentityPoolCredentials(logins) {
  const getIdRes = await cognitoClient.send(
    new GetIdCommand({
      IdentityPoolId: cfg.IdentityPoolId,
      Logins: logins
    })
  );

  _cachedIdentityId = getIdRes.IdentityId;

  const credsRes = await cognitoClient.send(
    new GetCredentialsForIdentityCommand({
      IdentityId: _cachedIdentityId,
      Logins: logins
    })
  );

  const c = credsRes.Credentials;
  _cachedCredentials = {
    credentials: {
      accessKeyId: c.AccessKeyId,
      secretAccessKey: c.SecretKey,
      sessionToken: c.SessionToken,
      expiration: c.Expiration
    },
    identityId: _cachedIdentityId
  };

  return _cachedCredentials;
}

// Build a Logins map from the User Pool ID token.
// Reads the token provider directly to avoid circular fetchAuthSession calls.
async function _getUserPoolLogins() {
  try {
    const tokens = await cognitoUserPoolsTokenProvider.getTokens();
    const idToken = tokens?.idToken?.toString();
    if (!idToken) return null;
    return { [COGNITO_PROVIDER]: idToken };
  } catch {
    return null;
  }
}

function _isExpired(creds) {
  if (!creds?.credentials?.expiration) return true;
  // Refresh 5 min before actual expiry
  return new Date(creds.credentials.expiration).getTime() - Date.now() < 5 * 60 * 1000;
}

// Called by Amplify to obtain AWS credentials.
export const customCredentialsProvider = {
  async getCredentialsAndIdentityId({ authenticated, forceRefresh }) {
    // Determine which Logins map to use
    const logins = _socialLogins || (authenticated ? await _getUserPoolLogins() : null);
    if (!logins) return undefined;

    if (!forceRefresh && _cachedCredentials && !_isExpired(_cachedCredentials)) {
      return _cachedCredentials;
    }

    return _resolveIdentityPoolCredentials(logins);
  },

  clearCredentialsAndIdentityId() {
    clearFederation();
  }
};
